import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getActivePortal, getUserData, updateUserData, isAuthenticated, getAuthHeaders } from '@/utils/auth';
import { shopAPI } from '@/api/shop';
import api from '@/services/api';

const UserProfileContext = createContext(null);

// 60-second cooldown window to prevent rapid redundant network requests across route transitions
const DEDUPE_TTL_MS = 60 * 1000;

export const useUserProfile = () => {
  const context = useContext(UserProfileContext);
  if (!context) {
    throw new Error('useUserProfile must be used within a UserProfileProvider');
  }
  return context;
};

export const UserProfileProvider = ({ children, initialData = null }) => {
  const [profile, setProfile] = useState(initialData?.profile || null);
  const [subscription, setSubscription] = useState(initialData?.subscription || null);
  const [inventory, setInventory] = useState(initialData?.inventory || []);
  const [loading, setLoading] = useState(false);

  const location = useLocation();

  // Timestamps of last successful fetches to guard against stampedes
  const lastProfileFetchRef = useRef({});
  const lastSubscriptionFetchRef = useRef(0);
  const lastInventoryFetchRef = useRef(0);

  // In-flight promise tracking for concurrent deduping
  const inFlightProfileRef = useRef({});
  const inFlightSubscriptionRef = useRef(null);
  const inFlightInventoryRef = useRef(null);

  // Persistent refs for latest state to keep callbacks referentially stable
  const profileRef = useRef(profile);
  useEffect(() => {
    profileRef.current = profile;
  }, [profile]);

  const subscriptionRef = useRef(subscription);
  useEffect(() => {
    subscriptionRef.current = subscription;
  }, [subscription]);

  const inventoryRef = useRef(inventory);
  useEffect(() => {
    inventoryRef.current = inventory;
  }, [inventory]);

  // Sync with initialData changes (hydration)
  useEffect(() => {
    if (initialData) {
      const now = Date.now();
      if (initialData.profile) {
        setProfile(initialData.profile);
        const active = getActivePortal();
        if (active) lastProfileFetchRef.current[active] = now;
      }
      if (initialData.subscription) {
        setSubscription(initialData.subscription);
        lastSubscriptionFetchRef.current = now;
      }
      if (initialData.inventory) {
        setInventory(initialData.inventory);
        lastInventoryFetchRef.current = now;
      }
    }
  }, [initialData]);

  // Reset caches on logout
  useEffect(() => {
    const handleLogout = () => {
      lastProfileFetchRef.current = {};
      lastSubscriptionFetchRef.current = 0;
      lastInventoryFetchRef.current = 0;
      inFlightProfileRef.current = {};
      inFlightSubscriptionRef.current = null;
      inFlightInventoryRef.current = null;
      setProfile(null);
      setSubscription(null);
      setInventory([]);
    };

    window.addEventListener('sessionLoggedOut', handleLogout);
    return () => window.removeEventListener('sessionLoggedOut', handleLogout);
  }, []);

  const refreshProfile = useCallback(async (overridePortal = null, optionsOrForce = false) => {
    let portal = overridePortal;
    let force = optionsOrForce;

    // Support flexible signatures: refreshProfile(true) or refreshProfile('student', { force: true })
    if (typeof portal === 'boolean' || (portal && typeof portal === 'object' && portal.force !== undefined)) {
      force = portal;
      portal = null;
    }
    portal = portal || getActivePortal();
    const isForce = typeof force === 'boolean' ? force : Boolean(force?.force);

    if (!isAuthenticated(portal)) return;

    const now = Date.now();
    const lastFetch = lastProfileFetchRef.current[portal] || 0;

    // Deduplication check: return cached profile if freshly fetched within TTL unless forced
    if (!isForce && profileRef.current && (now - lastFetch < DEDUPE_TTL_MS)) {
      return profileRef.current;
    }

    // Reuse existing in-flight promise for this portal
    if (inFlightProfileRef.current[portal]) {
      return inFlightProfileRef.current[portal];
    }

    setLoading(true);
    const fetchPromise = (async () => {
      try {
        const headers = getAuthHeaders(portal);
        const response = await api.get('users/profile/', { headers, portal });
        const data = response.data;
        lastProfileFetchRef.current[portal] = Date.now();
        setProfile(data);
        updateUserData(portal, data);
        return data;
      } catch (error) {
        console.error('[UserProfileContext] Failed to refresh profile:', error);
        throw error;
      } finally {
        setLoading(false);
        delete inFlightProfileRef.current[portal];
      }
    })();

    inFlightProfileRef.current[portal] = fetchPromise;
    return fetchPromise.catch(() => {});
  }, []);

  const refreshSubscription = useCallback(async (optionsOrForce = false) => {
    const portal = getActivePortal();
    if (!isAuthenticated(portal) || portal !== 'student') return;

    const isForce = typeof optionsOrForce === 'boolean' ? optionsOrForce : Boolean(optionsOrForce?.force);
    const now = Date.now();

    // Deduplication check: return cached subscription if freshly fetched within TTL unless forced
    if (!isForce && lastSubscriptionFetchRef.current > 0 && (now - lastSubscriptionFetchRef.current < DEDUPE_TTL_MS)) {
      return subscriptionRef.current;
    }

    // Reuse existing in-flight promise
    if (inFlightSubscriptionRef.current) {
      return inFlightSubscriptionRef.current;
    }

    const fetchPromise = (async () => {
      try {
        const response = await api.get('users/subscription/');
        lastSubscriptionFetchRef.current = Date.now();
        setSubscription(response.data);
        return response.data;
      } catch (error) {
        if (error?.response?.status === 404) {
          lastSubscriptionFetchRef.current = Date.now();
          setSubscription(null);
          return null;
        }
        console.error('[UserProfileContext] Failed to refresh subscription:', error);
        throw error;
      } finally {
        inFlightSubscriptionRef.current = null;
      }
    })();

    inFlightSubscriptionRef.current = fetchPromise;
    return fetchPromise.catch(() => {});
  }, []);

  const refreshInventory = useCallback(async (optionsOrForce = false) => {
    const portal = getActivePortal();
    if (!isAuthenticated(portal) || portal !== 'student') return;

    const isForce = typeof optionsOrForce === 'boolean' ? optionsOrForce : Boolean(optionsOrForce?.force);
    const now = Date.now();

    // Deduplication check: return cached inventory if freshly fetched within TTL unless forced
    if (!isForce && lastInventoryFetchRef.current > 0 && (now - lastInventoryFetchRef.current < DEDUPE_TTL_MS)) {
      return inventoryRef.current;
    }

    // Reuse existing in-flight promise
    if (inFlightInventoryRef.current) {
      return inFlightInventoryRef.current;
    }

    const fetchPromise = (async () => {
      try {
        const data = await shopAPI.getInventory();
        const items = Array.isArray(data) ? data : (data.results || data);
        lastInventoryFetchRef.current = Date.now();
        setInventory(items);
        return items;
      } catch (error) {
        console.error('[UserProfileContext] Failed to refresh inventory:', error);
        throw error;
      } finally {
        inFlightInventoryRef.current = null;
      }
    })();

    inFlightInventoryRef.current = fetchPromise;
    return fetchPromise.catch(() => {});
  }, []);

  // Listen for login success event to reset/refresh profile data with forced fresh fetch
  useEffect(() => {
    const handleLogin = (e) => {
      const targetPortal = e.detail?.portal || getActivePortal();
      console.log('[UserProfileContext] User logged in, refreshing data for portal:', targetPortal);
      lastProfileFetchRef.current[targetPortal] = 0;
      lastSubscriptionFetchRef.current = 0;
      lastInventoryFetchRef.current = 0;
      refreshProfile(targetPortal, true).catch(() => {});
      if (targetPortal === 'student') {
        refreshSubscription(true).catch(() => {});
        refreshInventory(true).catch(() => {});
      }
    };
    
    window.addEventListener('login_success', handleLogin);
    return () => window.removeEventListener('login_success', handleLogin);
  }, [refreshProfile, refreshSubscription, refreshInventory]);

  // Check profile when route changes or profile is missing (gated by DEDUPE_TTL_MS)
  useEffect(() => {
    const portal = getActivePortal();
    if (isAuthenticated(portal)) {
      refreshProfile(portal);
      if (portal === 'student') {
        refreshSubscription();
        refreshInventory();
      }
    }
  }, [location.pathname, refreshProfile, refreshSubscription, refreshInventory]);

  const value = {
    profile,
    subscription,
    inventory,
    loading,
    refreshProfile,
    refreshSubscription,
    refreshInventory,
    setProfile,
    setSubscription,
    setInventory
  };

  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  );
};
